import type { Router } from 'vue-router';
import { isLoggingOut } from '@/state/authState';
import { useAppConfigStore } from '@/stores/appConfig';
import { useCurrentUserStore } from '@/stores/currentUser';

export function setupGuards(router: Router): void {
    router.beforeEach(async (to) => {
        const appConfigStore = useAppConfigStore();
        const currentUserStore = useCurrentUserStore();

        if (to.matched.some(record => record.meta.requiresAuth === true)) {
            try {
                await currentUserStore.loadCurrentUser();
            } catch {
                return { name: 'login', query: { redirect: to.fullPath } };
            }

            if (currentUserStore.currentUser == null) {
                return { name: 'login', query: { redirect: to.fullPath } };
            }

            await appConfigStore.loadConfig();

            // Spark gates the whole authenticated app behind email verification;
            // the notice page itself has to stay reachable or the user is trapped.
            if (to.meta.skipVerified !== true && currentUserStore.currentUser.email_verified_at == null) {
                return { name: 'verification.notice' };
            }
        }

        if (to.matched.some(record => record.meta.requiresDeveloper === true)) {
            if (appConfigStore.appConfig?.isDeveloper !== true) {
                return { name: 'dashboard' };
            }
        }

        if (to.matched.some(record => record.meta.guest === true)) {
            // Mid-logout the user must be allowed to land on /login even though a
            // stale session cookie may still authenticate them.
            if (isLoggingOut.value) return true;

            try {
                await currentUserStore.loadCurrentUser();
            } catch {
                return true;
            }

            if (currentUserStore.currentUser != null) {
                return { name: 'dashboard' };
            }
        }

        return true;
    });
}
